import { FieldArrayRenderProps, useFormikContext } from 'formik';
import { cloneDeep, isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Scratchpad } from '../../../../../../../api/graphql/generated';
import i18n, { i18nKeyType } from '../../../../../../../i18n';
import { getLocalizedText } from '../../../../../../../i18n/i18n.utils';
import { logger } from '../../../../../../../util/logger';
import {
  AngleModificationMode,
  MotionPoint,
  MotionType,
  MultiPassSettings,
  MultipassOffsets,
  PartUtil,
  WorkPiecePosition,
} from '../../../../../../../util/part.util';
import {
  FlowUpdateMethod,
  useFlowRefContext,
} from '../../../../../shared/component/flow/flow-ref';
import { PickerOption } from '../../../../../shared/component/widget/widget.types';
import { useFlowServices } from '../../../../../shared/hook/flow-service.hooks';
import {
  ApplicationDefaultParameterSetData,
  Flow,
  FlowItem,
  FlowItemArrayType,
  FlowItemParameter,
  FlowItemSection,
  FlowItemSharedRef,
  FlowItemText,
} from '../../../../../shared/model/flow.types';
import {
  FlowItemSequence,
  FlowItemService,
  FlowService,
} from '../../../../../shared/service/service.types';
import {
  instanceOfFlowItem,
  instanceOfFlowItemSection,
  instanceOfPlaceholder,
} from '../../../../../shared/util/flow-item.util';
import { getParameterValue } from '../../../../../shared/util/flow-parameter.util';
import { BeaconWeldingData } from '../../../model/beacon-welding-data';
import { instanceOfFlowItemMultipassSectionParent } from '../../../util/beacon-welding.util';
import { getMotionPointsFromScheduleForm } from './multipass.utils';

// ===============================
// Types and Interfaces
// ===============================

export enum MultipassState {
  EDITING = 'editing',
  ERROR = 'error',
  SAVING = 'saving',
}

interface MultipassStatus {
  status: MultipassState;
  message?: i18nKeyType;
}

export interface MultipassComponentSettingsFormData
  extends BeaconWeldingData.MultipassSettingsFormData {
  // unpersisted properties
  rootPassOffsets: BeaconWeldingData.MultipassPointOffsetFormData;
}

interface MultipassFormControllerProps {
  scratchpad: Scratchpad; // Currently unused but kept for API compatibility
  startItem: FlowItem;
  onMultipassConverted: () => void;
}

export type RootPassOffsets = Omit<MultipassOffsets, 'mode'>;

export interface MultipassFormControllerResult {
  handleSubmit: (
    values: BeaconWeldingData.MultipassSettingsFormData
  ) => Promise<void>;
  initialFormValues: MultipassComponentSettingsFormData;
  isEditing: boolean;
  status: MultipassStatus;
  rootPass: FlowItem[];
}

interface FlowBoundaries {
  startIndex: number;
  endIndex: number;
}

interface OriginalSegment {
  type: 'original';
  points: MotionPoint[];
}

interface MultipassSegment {
  type: 'multipass';
  points: MotionPoint[][];
  defaultItem: FlowItem;
  originalItems: FlowItem[];
}

type MultipassedPart = OriginalSegment | MultipassSegment;

type MultipassedParts = MultipassedPart[];

// ===============================
// Main Hooks
// ===============================

const useMultipassFormController = (
  props: MultipassFormControllerProps
): MultipassFormControllerResult => {
  const { startItem, onMultipassConverted } = props;

  const { flow, flowItemService, flowService } = useFlowServices();
  const { saveFlow } = useFlowRefContext();

  const [status, setStatus] = useState<MultipassStatus>({
    status: MultipassState.EDITING,
  });

  const initialFormValues: MultipassComponentSettingsFormData =
    getInitialMultipassSettingsFormData({ startItem, flowItemService, flow });
  const isEditing = instanceOfFlowItemMultipassSectionParent(startItem);
  const currentMultipassItemCount = flow.items.filter(
    instanceOfFlowItemMultipassSectionParent
  ).length;
  const creationIndex = isEditing
    ? startItem.attributes.creationIndex
    : currentMultipassItemCount + 1;

  const convertToMultipass = useConvertToMultipassFlowItems({
    flow,
    flowItemService,
    flowService,
    startItem,
    creationIndex,
  });

  const rootPass = useMemo(() => {
    const originalItems = getOriginalItems({
      flow,
      flowItemService,
      startItem,
    });

    return originalItems.flowItems as FlowItem[];
  }, [flow, flowItemService, startItem]);

  const handleSubmit = useCallback(
    async (
      values: BeaconWeldingData.MultipassSettingsFormData
    ): Promise<void> => {
      const convertedValues = convertFormDataToActualValues(values);
      if (
        !isRequiredMultipassFormData(convertedValues) ||
        convertedValues.schedule.length < convertedValues.numberOfPasses
      ) {
        return;
      }

      if (
        ![MultipassState.EDITING, MultipassState.ERROR].includes(status.status)
      ) {
        return;
      }

      setStatus({
        status: MultipassState.SAVING,
      });

      const { flowItems: updatedFlowItems, error: convertToMultipassError } =
        await convertToMultipass({
          ...convertedValues,
          // this allows multipass sections being modified to be reduced to a smaller number of passes
          // e.g. 3 passes to 2 passes
          schedule: convertedValues.schedule.slice(0, values.numberOfPasses),
        });

      if (convertToMultipassError) {
        setStatus({
          status: MultipassState.ERROR,
          message: convertToMultipassError.userMessage,
        });

        if (convertToMultipassError.details) {
          logger.error(convertToMultipassError.details.message, {
            context: convertToMultipassError.details.context,
          });
        }

        return;
      }

      if (isNil(updatedFlowItems)) {
        // failed to generate multipass flow items
        setStatus({
          status: MultipassState.ERROR,
          message: i18n.t('welding:multipass_generation_validation_error'),
        });
        return;
      }

      saveFlow(updatedFlowItems, {
        updateMethod: FlowUpdateMethod.SERVER_WITH_OPTIMISTIC_UPDATE,
        saveAsDelta: true,
      });

      setStatus({
        status: MultipassState.EDITING,
      });

      onMultipassConverted();
    },
    [convertToMultipass, onMultipassConverted, saveFlow, status.status]
  );

  return {
    status,
    initialFormValues,
    isEditing,
    handleSubmit,
    rootPass,
  };
};

export const useMultipassScheduleFormHelpers = () => {
  const formik = useFormikContext<MultipassComponentSettingsFormData>();

  const getScheduleFieldName = useCallback(
    ({ fieldName, passIndex }: { fieldName: string; passIndex: number }) => {
      return `schedule[${passIndex}].${fieldName}`;
    },
    []
  );

  const setPassFormValues = useCallback(
    ({
      passIndex,
      data,
    }: {
      passIndex: number;
      data: Partial<BeaconWeldingData.MultipassPointOffsetFormData>;
    }) => {
      // Get current schedule array
      const currentSchedule = formik.values.schedule || [];
      // Create a new schedule array with the updated pass data
      const newSchedule = [...currentSchedule];
      newSchedule[passIndex] = {
        ...newSchedule[passIndex],
        ...data,
      };
      // Set the entire schedule array at once
      formik.setFieldValue('schedule', newSchedule);
    },
    [formik]
  );

  const helpers = useMemo(() => {
    return {
      getScheduleFieldName,
      setPassFormValues,
    };
  }, [getScheduleFieldName, setPassFormValues]);

  return helpers;
};

// ===============================
// Flow Conversion Hooks
// ===============================

interface UseConvertToMultipassFlowItemsProps {
  flow: Flow;
  flowItemService: FlowItemService;
  flowService: FlowService;
  startItem: FlowItem;
  creationIndex: number;
}

interface ConvertToMultipassFlowItemsErrorDetails {
  message: string;
  context?: Record<string, unknown>;
}

interface ConvertToMultipassFlowItemsResult {
  flowItems: FlowItem[] | undefined;
  error?: {
    userMessage: i18nKeyType;
    details?: ConvertToMultipassFlowItemsErrorDetails;
  };
}

type ConvertToMultipassFlowItems = (
  multipassFormData: Required<BeaconWeldingData.MultipassSettingsFormData>
) => Promise<ConvertToMultipassFlowItemsResult>;

const calculateFlowBoundaries = (
  flowItems: FlowItem[],
  startItem: FlowItem,
  multipassFormData: Required<BeaconWeldingData.MultipassSettingsFormData>
): FlowBoundaries => {
  const startIndex = flowItems.findIndex(
    (item) => item.id === multipassFormData.startWeldSequence.firstItem
  );
  const endIndex = flowItems.findIndex(
    (item) => item.id === multipassFormData.endWeldSequence.lastItem
  );

  return { startIndex, endIndex };
};

const getAllOriginalItemsFromMultipassed = (
  startItem: FlowItem,
  flow: Flow
): FlowItem[] => {
  let allOriginalItems = flow.items;
  const isEditing = instanceOfFlowItemMultipassSectionParent(startItem);
  if (isEditing) {
    // if editing, we need to put the flow items back in the original state
    const multipassStartIndex = flow.items.findIndex(
      (item) => item.id === startItem.id
    );
    const multipassEndIndex = flow.items.findIndex(
      (item) => item.id === startItem.attributes.endItemId
    );

    allOriginalItems = [
      ...allOriginalItems.slice(0, multipassStartIndex),
      ...(
        startItem as BeaconWeldingData.FlowItemMultipassSectionParent
      ).attributes.originalItems.filter(instanceOfFlowItem),
      ...allOriginalItems.slice(multipassEndIndex + 1),
    ];
  }

  return allOriginalItems;
};

const getOriginalItemsForMultipass = (
  startItem: FlowItem,
  multipassFormData: Required<BeaconWeldingData.MultipassSettingsFormData>,
  flow: Flow,
  flowItemService: FlowItemService
): FlowItemArrayType[] => {
  const allOriginalItems = getAllOriginalItemsFromMultipassed(startItem, flow);

  return getOriginalItemsFromStartIdAndEndId({
    flow: {
      ...flow,
      items: allOriginalItems,
    },
    flowItemService,
    startId: multipassFormData.startWeldSequence.firstItem,
    endId: multipassFormData.endWeldSequence.lastItem,
  });
};

const generateMultipassedParts = (
  originalFlowItems: FlowItem[],
  multipassFormData: Required<BeaconWeldingData.MultipassSettingsFormData>,
  flowItemService: FlowItemService
): MultipassedParts => {
  const multipassedParts: MultipassedParts = [];

  for (let i = 0; i < originalFlowItems.length; i++) {
    const item = originalFlowItems[i];

    if (item.ref === BeaconWeldingData.FlowItemRef.SECTION) {
      continue;
    }

    if (canMultipassFlowItem(item)) {
      const contiguousItems = flowItemService.getContiguousItemsForFlowItem(
        item,
        originalFlowItems
      );

      const multipassedPoints = buildMultipassPoints({
        weldSegment: contiguousItems,
        multipassFormData,
        flowItemService,
      });

      if (!multipassedPoints) {
        throw new Error('Failed to create a multipass segment.');
      }

      const multipassedSegmentPasses =
        splitMultipassPointsIntoPasses(multipassedPoints);

      multipassedParts.push({
        type: 'multipass',
        points: multipassedSegmentPasses,
        defaultItem: contiguousItems[0],
        originalItems: contiguousItems,
      });

      i = i + contiguousItems.length - 1;
    } else {
      const lastPart = multipassedParts[multipassedParts.length - 1];
      const motionPoint = flowItemService.convertFlowItemsToMotionPoints([
        item,
      ])[0];

      if (lastPart?.type === 'original') {
        lastPart.points.push(motionPoint);
      } else {
        multipassedParts.push({
          type: 'original',
          points: [motionPoint],
        });
      }
    }
  }

  return multipassedParts;
};

const generatePassFlowItems = (
  multipassedPartsArray: MultipassedParts,
  multipassFormData: Required<BeaconWeldingData.MultipassSettingsFormData>,
  flowItemService: FlowItemService
): FlowItem[] => {
  const finalizedMultipassFlowItems: FlowItem[] = [];

  for (let i = 0; i < multipassFormData.numberOfPasses; i++) {
    const passFlowItems: FlowItem[] = [];

    for (
      let partIndex = 0;
      partIndex < multipassedPartsArray.length;
      partIndex++
    ) {
      const part = multipassedPartsArray[partIndex];
      const isLastPart = partIndex === multipassedPartsArray.length - 1;

      if (part.type === 'multipass') {
        let sequencePoints = part.points[i];

        if (
          !isLastPart &&
          sequencePoints.length > 0 &&
          sequencePoints[sequencePoints.length - 1].type === MotionType.AIR
        ) {
          sequencePoints = sequencePoints.slice(0, -1);
        }

        const sequenceFlowItems = sequencePoints.map((point, index) => {
          const defaultItem =
            part.originalItems && part.originalItems[index]
              ? part.originalItems[index]
              : part.defaultItem;

          const sequenceDefaultItem = applyScheduleToDefaultItem({
            defaultItem,
            scheduleData: multipassFormData.schedule[i],
            flowItemService,
          });

          return flowItemService.convertMotionPointToFlowItem({
            point,
            defaultItem: sequenceDefaultItem,
          });
        });

        passFlowItems.push(...sequenceFlowItems);
      } else {
        const nonMultipassFlowItems = part.points.map((point) =>
          flowItemService.convertMotionPointToFlowItem({
            point,
            defaultItem: flowItemService.getDefaultItemForRef(
              BeaconWeldingData.FlowItemRef.MOVE
            ),
          })
        );

        passFlowItems.push(...nonMultipassFlowItems);
      }
    }

    const postActions = multipassFormData.schedule[i].postActions;
    if (postActions && postActions.length > 0) {
      passFlowItems.push(...postActions);
    }

    const passSection = createPassSection({
      passFlowItems,
      passIndex: i,
      flowItemService,
      multipassFormData,
    });

    finalizedMultipassFlowItems.push(...passSection);
  }

  return finalizedMultipassFlowItems;
};

const useConvertToMultipassFlowItems = (
  props: UseConvertToMultipassFlowItemsProps
): ConvertToMultipassFlowItems => {
  const { flow, flowItemService, flowService, startItem, creationIndex } =
    props;

  const isEditing = instanceOfFlowItemMultipassSectionParent(startItem);

  const convertToMultipassFlowItems: ConvertToMultipassFlowItems = useCallback(
    async (
      multipassFormData: Required<BeaconWeldingData.MultipassSettingsFormData>
    ): Promise<ConvertToMultipassFlowItemsResult> => {
      const originalItems = getOriginalItemsForMultipass(
        startItem,
        multipassFormData,
        flow,
        flowItemService
      );

      const originalFlowItems = originalItems.filter(instanceOfFlowItem);

      let multipassedParts: MultipassedParts;

      try {
        multipassedParts = generateMultipassedParts(
          originalFlowItems,
          multipassFormData,
          flowItemService
        );
      } catch (error) {
        return {
          flowItems: undefined,
          error: {
            userMessage: i18n.t('welding:multipass_generation_standard_error'),
            details: {
              message: error instanceof Error ? error.message : 'Unknown error',
              context: { originalFlowItems, multipassFormData },
            },
          },
        };
      }

      const finalizedMultipassFlowItems = generatePassFlowItems(
        multipassedParts,
        multipassFormData,
        flowItemService
      );

      const rootPassFlowItems = createRootPassFlowItems(
        originalItems,
        flowItemService,
        multipassFormData.rootPassPostActions
      );
      const allMultipassFlowItems = [
        ...rootPassFlowItems,
        ...finalizedMultipassFlowItems,
      ];

      const allOriginalItems = getAllOriginalItemsFromMultipassed(
        startItem,
        flow
      );
      let startIndex: number;
      let endIndex: number;

      try {
        const boundaries = calculateFlowBoundaries(
          allOriginalItems,
          startItem,
          multipassFormData
        );
        startIndex = boundaries.startIndex;
        endIndex = boundaries.endIndex;
      } catch (error) {
        return {
          flowItems: undefined,
          error: {
            userMessage: i18n.t('welding:multipass_generation_standard_error'),
            details: {
              message: error instanceof Error ? error.message : 'Unknown error',
              context: { startItem, multipassFormData },
            },
          },
        };
      }

      const flowValidationErrors = await flowService.validate({
        ...flow,
        items: finalizedMultipassFlowItems,
      });

      if (!isEmpty(flowValidationErrors)) {
        return {
          flowItems: undefined,
          error: {
            userMessage: i18n.t(
              'welding:multipass_generation_validation_error'
            ),
            details: {
              message: 'Failed to validate multipass flow items.',
              context: { flowValidationErrors },
            },
          },
        };
      }

      const nestedMultipassFlowItems = nestMultipassItemsUnderSectionParent({
        flowItems: allOriginalItems,
        flowItemService,
        sectionNumericPrefix: creationIndex,
        multipassFormData,
        multipassFlowItems: allMultipassFlowItems,
        originalItems,
        isEditing,
        startIndex,
        endIndex,
      });

      return {
        flowItems: nestedMultipassFlowItems,
      };
    },
    [creationIndex, flow, flowItemService, flowService, isEditing, startItem]
  );

  return convertToMultipassFlowItems;
};

const useManageMultipassScheduleFieldArray = (
  fieldArrayProps: FieldArrayRenderProps
) => {
  const { values } =
    useFormikContext<BeaconWeldingData.MultipassSettingsFormData>();

  const scheduleLength = values.schedule?.length ?? 0;

  useEffect(() => {
    if (isNil(values.numberOfPasses)) {
      return;
    }

    const numberOfPasses = Number(values.numberOfPasses);

    if (scheduleLength < numberOfPasses) {
      const diff = numberOfPasses - scheduleLength;
      for (let i = 0; i < diff; i++) {
        fieldArrayProps.push({});
      }
      return;
    } else if (scheduleLength > numberOfPasses) {
      const diff = scheduleLength - numberOfPasses;
      for (let i = 0; i < diff; i++) {
        fieldArrayProps.pop();
      }
    }
  }, [fieldArrayProps, scheduleLength, values.numberOfPasses]);
};

// ===============================
// Utility Functions
// ===============================

const buildMultipassPoints = (params: {
  weldSegment: FlowItem[];
  multipassFormData: Required<BeaconWeldingData.MultipassSettingsFormData>;
  flowItemService: FlowItemService;
}): MotionPoint[] | undefined => {
  const { weldSegment, multipassFormData, flowItemService } = params;

  const defaultItem = getDefaultStartItem({
    startItem: weldSegment[0],
    originalItems: weldSegment,
  });

  if (!defaultItem) {
    return;
  }

  const rootMotionPoints = getMotionPoints({
    originalItems: weldSegment,
    flowItemService,
  });

  const passMotionPoints = generateMultipassMotionPoints({
    motionPoints: rootMotionPoints,
    formData: multipassFormData,
  });

  return passMotionPoints;
};

const splitMultipassPointsIntoPasses = (
  multipassedPoints: MotionPoint[]
): MotionPoint[][] => {
  const passes: MotionPoint[][] = [[]];
  for (let i = 0; i < multipassedPoints.length; i++) {
    const point = multipassedPoints[i];

    passes[passes.length - 1].push(point);
    const shouldCreateNewPass =
      point.type === MotionType.AIR && i < multipassedPoints.length - 1;

    if (shouldCreateNewPass) {
      passes.push([]);
    }
  }

  return passes;
};

const isRequiredMultipassFormData = (
  formData: BeaconWeldingData.MultipassSettingsFormData
): formData is Required<BeaconWeldingData.MultipassSettingsFormData> => {
  const { numberOfPasses, schedule, angleMode, workpiecePosition } = formData;

  return (
    !isNil(numberOfPasses) &&
    numberOfPasses > 0 &&
    !isNil(schedule) &&
    !isNil(angleMode) &&
    !isNil(workpiecePosition)
  );
};

const getDefaultStartItem = (params: {
  startItem: FlowItem;
  originalItems: FlowItemArrayType[];
}): FlowItem | undefined => {
  const { startItem, originalItems } = params;
  const isEditing = instanceOfFlowItemMultipassSectionParent(startItem);

  if (!isEditing) {
    return startItem;
  }

  for (const item of originalItems) {
    if (!instanceOfFlowItem(item)) {
      continue;
    }

    if (item.ref === BeaconWeldingData.FlowItemRef.WELD) {
      return item;
    }
  }
};

const getOriginalItemsFromStartIdAndEndId = (params: {
  flow: Flow;
  flowItemService: FlowItemService;
  startId: string;
  endId: string;
}): FlowItemArrayType[] => {
  const { flow, startId, endId } = params;

  const startIndex = flow.items.findIndex((item) => item.id === startId);
  const endIndex = flow.items.findIndex((item) => item.id === endId);

  if (startIndex === -1 || endIndex === -1) {
    return [];
  }

  const originalItems = flow.items.slice(startIndex, endIndex + 1);
  const previousItem = flow.items[startIndex - 1];

  // If the previous item is a section, we need to add it to the original items
  if (previousItem && instanceOfFlowItemSection(previousItem)) {
    originalItems.unshift(previousItem);
  }

  return originalItems.filter(instanceOfFlowItem);
};

interface GetOriginalItemsParams {
  flow: Flow;
  flowItemService: FlowItemService;
  startItem: FlowItem;
}

interface GetOriginalItemsResultBase {
  flowItems?: FlowItemArrayType[];
  errorMessage?: string;
}

interface GetOriginalItemsSuccess extends GetOriginalItemsResultBase {
  flowItems: FlowItemArrayType[];
  errorMessage: undefined;
}

interface GetOriginalItemsError extends GetOriginalItemsResultBase {
  flowItems: undefined;
  errorMessage: string;
}

const getOriginalItems = (
  params: GetOriginalItemsParams
): GetOriginalItemsSuccess | GetOriginalItemsError => {
  const { flow, flowItemService, startItem } = params;

  const isEditing = instanceOfFlowItemMultipassSectionParent(startItem);

  if (isEditing) {
    return {
      flowItems: startItem.attributes.originalItems,
      errorMessage: undefined,
    };
  }

  const startIndex = flowItemService.getStartOfSequenceIndexFromFlowItem(
    startItem,
    flow.items
  );
  // This will change if we allow the user to define the end item
  const endIndex = flowItemService.getEndOfSequenceIndexFromFlowItem(
    startItem,
    flow.items
  );

  if (startIndex === -1) {
    return {
      flowItems: undefined,
      errorMessage:
        'Cannot process multipass. No start index was found for the segment.',
    };
  }

  if (isNil(endIndex)) {
    return {
      flowItems: undefined,
      errorMessage:
        'Cannot process multipass. No end index was found for the segment.',
    };
  }

  // we will include the encapsulating section if it exists
  const previousItem = startIndex ? flow.items[startIndex - 1] : undefined;
  const segment = cloneDeep(flow.items.slice(startIndex, endIndex + 1));
  const originalItems: FlowItemArrayType[] =
    !isNil(previousItem) && instanceOfFlowItemSection(previousItem)
      ? [cloneDeep(previousItem), ...segment]
      : segment;

  return {
    flowItems: originalItems,
    errorMessage: undefined,
  };
};

export const getMotionPoints = (params: {
  originalItems: FlowItemArrayType[];
  flowItemService: FlowItemService;
}): MotionPoint[] => {
  const { originalItems, flowItemService } = params;

  const originalPathItems = originalItems
    .filter(instanceOfFlowItem)
    .filter((i) => !instanceOfFlowItemSection(i));

  const motionPoints: MotionPoint[] =
    flowItemService.convertFlowItemsToMotionPoints(originalPathItems, {
      addStickoutToPose: true,
    });

  return motionPoints;
};

const getInitialMultipassSettingsFormData = (params: {
  startItem: FlowItem;
  flowItemService: FlowItemService;
  flow: Flow;
}): MultipassComponentSettingsFormData => {
  const { startItem, flowItemService, flow } = params;
  const isEditing = instanceOfFlowItemMultipassSectionParent(startItem);
  const rootPassOffsets = getRootPassOffsetsFromStartItem(
    startItem,
    flowItemService,
    flow
  );
  const endItem = flowItemService.getEndOfSequenceItemFromFlowItem(
    startItem,
    flow.items
  );

  const defaultSequenceData: BeaconWeldingData.WeldSequenceMarkers = {
    firstItem: startItem.id as string,
    lastItem: endItem?.id as string,
  };

  let defaultEditingSequenceData:
    | BeaconWeldingData.WeldSequenceMarkers
    | undefined;

  if (isEditing) {
    const originalFlowItems =
      startItem.attributes.originalItems.filter(instanceOfFlowItem);
    const firstWeldItem = originalFlowItems.find(
      (item) => !instanceOfFlowItemSection(item)
    );

    const lastItem = originalFlowItems[originalFlowItems.length - 1];

    if (firstWeldItem && lastItem) {
      defaultEditingSequenceData = {
        firstItem: firstWeldItem.id as string,
        lastItem: lastItem.id as string,
      };
    }
  }

  const initialMultipassSettingsValues: MultipassComponentSettingsFormData =
    isEditing
      ? {
          ...startItem.attributes.multipassSettings,
          rootPassOffsets,
          startWeldSequence:
            startItem.attributes.multipassSettings.startWeldSequence ??
            defaultEditingSequenceData,
          endWeldSequence:
            startItem.attributes.multipassSettings.endWeldSequence ??
            defaultEditingSequenceData,
        }
      : {
          reference: undefined,
          numberOfPasses: 1,
          workpiecePosition: WorkPiecePosition.HORIZONTAL,
          angleMode: AngleModificationMode.RELATIVE,
          schedule: [],
          rootPassOffsets,
          startWeldSequence: defaultSequenceData,
          endWeldSequence: defaultSequenceData,
        };

  return initialMultipassSettingsValues;
};

const getRootPassOffsetsFromStartItem = (
  startItem: FlowItem,
  flowItemService: FlowItemService,
  flow: Flow
): BeaconWeldingData.MultipassPointOffsetFormData => {
  let item: FlowItem | undefined = startItem;
  if (instanceOfFlowItemMultipassSectionParent(startItem)) {
    item = getDefaultStartItem({
      startItem,
      originalItems: startItem.attributes.originalItems,
    });

    if (!item) {
      logger.error(
        'Failed to get root pass offsets. Could not get the actual start item to calculate angles.',
        {
          startItem,
        }
      );
    }
  }

  const fineTuneProps = item
    ? flowItemService.getFineTuneProps(flow, item)
    : undefined;
  const fineTuneAngles = fineTuneProps
    ? flowItemService.getFineTuneAngles(fineTuneProps)
    : undefined;

  const travelAngle = Number(fineTuneAngles?.travelAngle) || 0;
  const workAngle = Number(fineTuneAngles?.workAngle) || 0;

  const offsets: BeaconWeldingData.MultipassPointOffsetFormData = {
    startOffsetX: 0,
    endOffsetX: 0,
    perpendicularToPathY: 0,
    stickoutZ: 0,
    travelAngle,
    workAngle,
  };

  return offsets;
};

const generateMultipassMotionPoints = (params: {
  motionPoints: MotionPoint[];
  formData: Required<BeaconWeldingData.MultipassSettingsFormData>;
}): MotionPoint[] => {
  const { motionPoints, formData } = params;

  const multipassSettings: MultiPassSettings[] =
    convertMultipassFormToMultiPassSettings(formData);

  return PartUtil.generateMultiPass(motionPoints, multipassSettings);
};

// ===============================
// Data Transformation Functions
// ===============================

const convertFormDataToActualValues = (
  multipassFormData: BeaconWeldingData.MultipassSettingsFormData
): BeaconWeldingData.MultipassSettingsFormData => {
  const {
    numberOfPasses,
    schedule,
    angleMode,
    workpiecePosition,
    reference,
    rootPassPostActions,
  } = multipassFormData;

  return {
    numberOfPasses,
    schedule: schedule?.map((pass) =>
      convertMultipassPointOffsetFormDataToActualValues(pass)
    ),
    startWeldSequence: {
      firstItem: multipassFormData.startWeldSequence.firstItem,
      lastItem: multipassFormData.startWeldSequence.lastItem,
    },
    endWeldSequence: {
      firstItem: multipassFormData.endWeldSequence.firstItem,
      lastItem: multipassFormData.endWeldSequence.lastItem,
    },
    angleMode,
    workpiecePosition,
    reference,
    rootPassPostActions,
  };
};

const convertMultipassFormToMultiPassSettings = (
  form: Required<BeaconWeldingData.MultipassSettingsFormData>
): MultiPassSettings[] => {
  const { angleMode, workpiecePosition, schedule } = form;

  return schedule.map<MultiPassSettings>((pass) => {
    const passOffsets = convertMultipassPointOffsetFormDataToMultipassOffsets({
      pointOffsetFormData: pass,
      angleMode,
    });

    return {
      passDirection: pass.reversePath ? 'reverse' : 'forward',
      referencePass: 'root',
      partPosition: workpiecePosition,
      passOffsets,
    };
  });
};

const convertMultipassPointOffsetFormDataToMultipassOffsets = (params: {
  pointOffsetFormData: BeaconWeldingData.MultipassPointOffsetFormData;
  angleMode: AngleModificationMode;
}): MultipassOffsets => {
  const { pointOffsetFormData, angleMode } = params;

  try {
    const offsets: MultipassOffsets = {
      startAlongPath: pointOffsetFormData.startOffsetX,
      endAlongPath: pointOffsetFormData.endOffsetX,
      lateralToPath: pointOffsetFormData.perpendicularToPathY,
      alongTorchWire: pointOffsetFormData.stickoutZ,
      travelAngle: pointOffsetFormData.travelAngle,
      workAngle: pointOffsetFormData.workAngle,
      mode: angleMode,
    };

    return offsets;
  } catch (error) {
    logger.error('Failed to convert multipass point offset form data.', {
      error,
      params,
    });

    throw new Error('Failed to convert multipass point offset form data.');
  }
};

const convertMultipassPointOffsetFormDataToActualValues = (
  offsetFormData: BeaconWeldingData.MultipassPointOffsetFormData
): BeaconWeldingData.MultipassPointOffsetFormData => {
  const {
    startOffsetX,
    endOffsetX,
    perpendicularToPathY,
    travelAngle,
    workAngle,
    stickoutZ,
    reversePath,
    weldLibrary,
    postActions,
  } = offsetFormData;

  return {
    startOffsetX: Number(startOffsetX),
    endOffsetX: Number(endOffsetX),
    perpendicularToPathY: Number(perpendicularToPathY),
    travelAngle: Number(travelAngle),
    workAngle: Number(workAngle),
    stickoutZ: Number(stickoutZ),
    reversePath: reversePath ?? false,
    weldLibrary,
    postActions,
  };
};

// ===============================
// Flow Structure Functions
// ===============================

interface NestMultipassItemsUnderSectionParentParams {
  flowItems: FlowItem[];
  flowItemService: FlowItemService;
  sectionNumericPrefix: number;
  multipassFormData: Required<BeaconWeldingData.MultipassSettingsFormData>;
  multipassFlowItems: FlowItem[];
  originalItems: FlowItemArrayType[];
  isEditing: boolean;
  startIndex: number;
  endIndex: number;
}

const nestMultipassItemsUnderSectionParent = (
  params: NestMultipassItemsUnderSectionParentParams
): FlowItem[] => {
  const {
    flowItems,
    flowItemService,
    sectionNumericPrefix,
    multipassFormData,
    multipassFlowItems,
    originalItems,
    isEditing,
    startIndex,
    endIndex,
  } = params;

  const defaultParentSectionItem =
    flowItemService.getDefaultItemForRef<BeaconWeldingData.FlowItemMultipassSectionParentAttributes>(
      FlowItemSharedRef.SECTION
    );

  const parentSectionItem: BeaconWeldingData.FlowItemMultipassSectionParent = {
    ...defaultParentSectionItem,
    id: uuid(),
    name: `${getLocalizedText('welding:multipass')} ${sectionNumericPrefix}`,
    attributes: {
      isParent: true,
      type: BeaconWeldingData.FlowSectionType.MULTIPASS,
      endItemId: multipassFlowItems[multipassFlowItems.length - 1].id,
      originalItems,
      creationIndex: sectionNumericPrefix,
      multipassSettings: multipassFormData,
    },
  };

  const middle = [parentSectionItem, ...multipassFlowItems];

  // this is if the first item is the start of a section, if so we need to remove the section
  const isInSection =
    !isEditing &&
    startIndex > 0 &&
    flowItems[startIndex - 1]?.ref === FlowItemSharedRef.SECTION;

  const modifiedStartIndex = isInSection ? startIndex - 1 : startIndex;
  const start = flowItems.slice(0, modifiedStartIndex);
  const end = flowItems.slice(endIndex + 1);

  const updatedFlow: FlowItem[] = [...start, ...middle, ...end];
  return updatedFlow;
};

interface NestMultipassItemsUnderSectionParams {
  flowItemService: FlowItemService;
  sectionName: string;
  multipassFlowItems: FlowItem[];
}

const nestOriginalItemsUnderMultipassSection = (
  params: NestMultipassItemsUnderSectionParams
): FlowItem[] => {
  const { flowItemService, sectionName, multipassFlowItems } = params;

  const defaultSectionItem =
    flowItemService.getDefaultItemForRef<BeaconWeldingData.FlowItemMultipassSectionParentAttributes>(
      FlowItemSharedRef.SECTION
    );

  const sectionItem: BeaconWeldingData.FlowItemMultipassSection = {
    ...defaultSectionItem,
    id: uuid(),
    name: sectionName,
    attributes: {
      type: BeaconWeldingData.FlowSectionType.MULTIPASS,
    },
  };
  // this is if the first item is the start of a section, if so we need to remove the section
  const isInSection = multipassFlowItems[0].ref === FlowItemSharedRef.SECTION;

  const actionItems = isInSection
    ? multipassFlowItems.slice(1)
    : multipassFlowItems;
  const updatedFlow: FlowItem[] = [sectionItem, ...actionItems];

  return updatedFlow;
};

/**
 * Hook to generate a flow for a specific pass in a multipass weld.
 * Returns a callback that can be used to generate a flow for any pass.
 * @returns A callback function that takes the root pass and a single pass's form data and returns the flow for that pass
 */
export const useCreateFlowFromPass = (): ((
  rootPass: FlowItemArrayType[],
  passFormData: {
    schedule: BeaconWeldingData.MultipassPointOffsetFormData;
    angleMode?: AngleModificationMode;
    workpiecePosition: WorkPiecePosition;
  }
) => Flow | undefined) => {
  const { flowItemService, flow } = useFlowServices();

  return useCallback(
    (
      rootPass: FlowItemArrayType[],
      passFormData: {
        schedule: BeaconWeldingData.MultipassPointOffsetFormData;
        angleMode?: AngleModificationMode;
        workpiecePosition: WorkPiecePosition;
      }
    ) => {
      // Validate inputs
      if (!rootPass?.length || !passFormData?.schedule) {
        return undefined;
      }

      try {
        // Deep clone the root pass to avoid mutations
        const clonedRootPass = cloneDeep(rootPass);

        // Get motion points from root pass
        const rootPassMotionPoints = getMotionPoints({
          originalItems: clonedRootPass,
          flowItemService,
        });

        // Generate motion points for this pass using the schedule
        const passMotionPoints = getMotionPointsFromScheduleForm({
          rootPassMotionPoints,
          form: passFormData.schedule,
          angleMode: passFormData.angleMode ?? AngleModificationMode.RELATIVE,
          workpiecePosition: passFormData.workpiecePosition,
        });

        // Get the first non-section FlowItem to use as default
        const defaultItem = clonedRootPass.find(
          (item): item is FlowItem =>
            instanceOfFlowItem(item) && !instanceOfFlowItemSection(item)
        );
        if (!defaultItem) {
          logger.error('No valid default item found in root pass');
          return undefined;
        }

        // Convert motion points back to flow items
        const passFlowItems = generatePassFlowItems(
          [
            {
              type: 'multipass',
              points: [passMotionPoints],
              defaultItem, // Use first non-section item as default
              originalItems: clonedRootPass.filter(instanceOfFlowItem),
            },
          ],
          {
            numberOfPasses: 1,
            schedule: [passFormData.schedule], // We only need this pass's schedule
            angleMode: passFormData.angleMode ?? AngleModificationMode.RELATIVE,
            workpiecePosition: passFormData.workpiecePosition,
            startWeldSequence: { firstItem: '', lastItem: '' }, // These aren't used for single pass generation
            endWeldSequence: { firstItem: '', lastItem: '' },
            reference: { id: 'temp', version: 1 }, // Temporary reference for single pass generation
            rootPassPostActions: [],
          },
          flowItemService
        );

        const items = flowItemService.recalculateParameterValues(
          flow.config,
          passFlowItems
        );

        // Create a new flow with the same config but only this pass's items
        const passFlow: Flow = {
          ...cloneDeep(flow),
          items,
        };

        return passFlow;
      } catch (error) {
        logger.error('Error generating pass flow', {
          error,
        });
        return undefined;
      }
    },
    [flowItemService, flow]
  );
};

const canMultipassFlowItem = (item: FlowItem): boolean => {
  const multipassableWeldTypes = [
    BeaconWeldingData.WeldTypeEnum.LINEAR,
    BeaconWeldingData.WeldTypeEnum.RADIAL,
    BeaconWeldingData.WeldTypeEnum.ROTARY,
  ];

  const weldType = getParameterValue<BeaconWeldingData.WeldTypeEnum>(
    item,
    BeaconWeldingData.FlowItemParameterRef.WELD_TYPE
  );

  return multipassableWeldTypes.includes(weldType);
};

const applyScheduleToDefaultItem = (params: {
  defaultItem: FlowItem;
  scheduleData: BeaconWeldingData.MultipassPointOffsetFormData;
  flowItemService: FlowItemService;
}): FlowItem => {
  const { defaultItem, scheduleData, flowItemService } = params;

  let sequenceDefaultItem: FlowItem = cloneDeep(defaultItem);

  // Apply weld library if specified
  if (scheduleData.weldLibrary) {
    const defaultParameterSetData: ApplicationDefaultParameterSetData =
      scheduleData.weldLibrary.data;
    const weldLibraryParameters: FlowItemParameter[] =
      defaultParameterSetData.parameters;

    sequenceDefaultItem = {
      ref: BeaconWeldingData.FlowItemRef.WELD,
      parameters: cloneDeep(weldLibraryParameters),
    };
  }

  sequenceDefaultItem = flowItemService.setParameterValue(
    sequenceDefaultItem,
    BeaconWeldingData.FlowItemParameterRef.STICKOUT,
    0
  );

  return sequenceDefaultItem;
};

const createPassSection = (params: {
  passFlowItems: FlowItem[];
  passIndex: number;
  flowItemService: FlowItemService;
  multipassFormData: Required<BeaconWeldingData.MultipassSettingsFormData>;
}): FlowItem[] => {
  const { passFlowItems, passIndex, flowItemService, multipassFormData } =
    params;

  const defaultSectionItem =
    flowItemService.getDefaultItemForRef<BeaconWeldingData.FlowItemMultipassSectionParentAttributes>(
      FlowItemSharedRef.SECTION
    );

  const sectionItem: BeaconWeldingData.FlowItemMultipassSection = {
    ...defaultSectionItem,
    id: uuid(),
    name:
      i18n.t('common:pass_with_number', {
        number: passIndex + 2,
      }) +
      (multipassFormData.schedule[passIndex]?.reversePath
        ? ` - (${i18n.t('common:reversed')})`
        : ''),
    attributes: {
      type: BeaconWeldingData.FlowSectionType.MULTIPASS,
    },
  };

  return [sectionItem, ...passFlowItems];
};

const createRootPassFlowItems = (
  originalItems: FlowItemArrayType[],
  flowItemService: FlowItemService,
  rootPassPostActions?: FlowItem[]
): FlowItem[] => {
  const rootMotionPoints = getMotionPoints({
    originalItems,
    flowItemService,
  });

  const rootDepartMotionPoint = PartUtil.createAirPoint(
    rootMotionPoints[rootMotionPoints.length - 1]
  );

  const rootDepartFlowItem = flowItemService.convertMotionPointToFlowItem({
    point: rootDepartMotionPoint,
    defaultItem: flowItemService.getDefaultItemForRef(
      BeaconWeldingData.FlowItemRef.MOVE
    ),
  });

  // Build the root pass flow items: original items + post actions + air depart
  // Filter out any post actions that might already be in originalItems to prevent duplication
  const postActionIds = new Set(
    rootPassPostActions?.map((action) => action.id) || []
  );
  const cleanOriginalItems = originalItems
    .filter(instanceOfFlowItem)
    .filter((item) => !item.id || !postActionIds.has(item.id));

  const rootPassFlowItems = [
    ...cleanOriginalItems,
    rootDepartFlowItem,
    ...(rootPassPostActions || []),
  ];

  return nestOriginalItemsUnderMultipassSection({
    flowItemService,
    sectionName: i18n.t('welding:multipass_root_pass'),
    multipassFlowItems: rootPassFlowItems,
  });
};

const generateWeldSequences = (
  flowItemService: FlowItemService,
  flowItems: FlowItem[]
): FlowItemSequence[] => {
  const weldSequences: FlowItemSequence[] = [];
  for (let i = 0; i < flowItems.length; i++) {
    const item = flowItems[i];

    if (
      item.ref === BeaconWeldingData.FlowItemRef.WELD &&
      canMultipassFlowItem(item)
    ) {
      const contiguousItems = flowItemService.getContiguousItemsForFlowItem(
        item,
        flowItems
      );

      const previousItem: FlowItem | undefined = flowItems[i - 1];
      let section: FlowItemSection | undefined = undefined;
      if (previousItem && instanceOfFlowItemSection(previousItem)) {
        section = cloneDeep(previousItem);
      }

      weldSequences.push({
        items: contiguousItems,
        section,
      });

      i += contiguousItems.length - 1;
    }
  }

  return weldSequences;
};

const getWeldSequenceOptions = (params: {
  flowItemService: FlowItemService;
  originalFlowItems: FlowItem[] | undefined;
  startItemId: string;
  originalTextArr: (FlowItemText | undefined)[];
  updatedTextMap: Record<string, FlowItemText>;
  multipassOriginalItems?: FlowItem[];
}): PickerOption[] => {
  const {
    flowItemService,
    originalFlowItems,
    startItemId,
    originalTextArr,
    updatedTextMap,
    multipassOriginalItems,
  } = params;

  const flowItems = originalFlowItems ?? [];
  const weldSequences = generateWeldSequences(flowItemService, flowItems);

  // Find index of start item in original items
  const startItemIndex =
    originalFlowItems?.findIndex((item) => item.id === startItemId) ?? 0;

  // Find the first parent section index (if any) - single pass optimization
  const firstParentSectionIndex = originalFlowItems?.findIndex(
    (item, idx) =>
      idx > startItemIndex && instanceOfFlowItemMultipassSectionParent(item)
  );

  return (
    weldSequences
      ?.map((sequence) => {
        const firstItem = sequence.items[0];
        const lastItem = sequence.items[sequence.items.length - 1];

        // Skip if first item is a section
        if (instanceOfFlowItemSection(firstItem)) {
          return null;
        }

        // Find index of first item in sequence
        const sequenceStartIndex = originalFlowItems?.findIndex(
          (item) => item.id === firstItem.id
        );

        // Skip if we can't find the sequence start index
        if (typeof sequenceStartIndex !== 'number') {
          return null;
        }

        // Skip if sequence starts before the start item
        const isSequenceBeforeStartItem =
          typeof startItemIndex === 'number' &&
          sequenceStartIndex < startItemIndex;
        if (isSequenceBeforeStartItem) {
          return null;
        }

        // Skip if there's a parent section that blocks this sequence
        const isParentSectionBlockingSequence =
          typeof firstParentSectionIndex === 'number' &&
          typeof startItemIndex === 'number' &&
          firstParentSectionIndex >= 0 &&
          sequenceStartIndex > firstParentSectionIndex &&
          startItemIndex <= firstParentSectionIndex;
        if (isParentSectionBlockingSequence) {
          return null;
        }

        const label = getFirstItemLabel(
          firstItem,
          startItemId,
          originalTextArr,
          updatedTextMap,
          multipassOriginalItems
        );

        return {
          label,
          value: {
            firstItem: firstItem.id,
            lastItem: lastItem.id,
          },
        } as PickerOption;
      })
      .filter((option): option is PickerOption => option !== null) ?? []
  );
};

// ===============================
// Helper Functions
// ===============================

function getFirstItemLabel(
  firstItem: FlowItem,
  startItemId: string,
  originalTextArr: (FlowItemText | undefined)[],
  updatedTextMap: Record<string, FlowItemText>,
  multipassOriginalItems?: FlowItem[]
): string | undefined {
  let firstItemText: FlowItemText | undefined;

  // Check if the firstItem is one of the original points of the multipass
  // by checking if it exists in the multipass section's originalItems
  const isOriginalMultipassItem = multipassOriginalItems?.some(
    (originalItem) => originalItem.id === firstItem.id
  );

  if (isOriginalMultipassItem) {
    // If it's an original multipass item, look for its label in originalTextArr
    const originalItemText = originalTextArr?.find((itemText) => {
      return itemText?.itemId === firstItem.id;
    });
    firstItemText = originalItemText;
  } else {
    // Otherwise, use the updated text from the current flow
    firstItemText = updatedTextMap[firstItem?.id ?? ''];
  }

  if (!firstItemText) {
    return undefined;
  }

  return firstItemText.label.replace(/(\d+)[a-z]+ -/, '$1 -');
}

// ===============================
// Multipass Regeneration Hook
// ===============================

interface UseRegenerateMultipassSectionProps {
  flowItemService: FlowItemService;
}

export const useRegenerateMultipassSection = (
  props: UseRegenerateMultipassSectionProps
) => {
  const { flowItemService } = props;

  const regenerateMultipassSection = useCallback(
    async (
      flowItems: FlowItem[],
      multipassSection: BeaconWeldingData.FlowItemMultipassSectionParent,
      updatedRootItems: FlowItem[]
    ): Promise<FlowItem[]> => {
      try {
        logger.info('Regenerating multipass section', {
          sectionId: multipassSection.id,
          updatedItemCount: updatedRootItems.length,
        });

        // Get the current multipass settings from the section
        const multipassSettings = multipassSection.attributes.multipassSettings;

        // Update the original items with the new values
        const updatedOriginalItems =
          multipassSection.attributes.originalItems.map((originalItem) => {
            if (instanceOfFlowItem(originalItem)) {
              const updatedItem = updatedRootItems.find(
                (item) => item.id === originalItem.id
              );
              return updatedItem || originalItem;
            }
            return originalItem;
          });

        // Generate the new multipass structure
        const multipassedParts = generateMultipassedParts(
          updatedOriginalItems.filter(instanceOfFlowItem),
          multipassSettings,
          flowItemService
        );

        const newPassFlowItems = generatePassFlowItems(
          multipassedParts,
          multipassSettings,
          flowItemService
        );

        // Create the root pass flow items
        const rootPassFlowItems = createRootPassFlowItems(
          updatedOriginalItems,
          flowItemService
        );

        // Combine root pass and additional passes
        const allMultipassFlowItems = [
          ...rootPassFlowItems,
          ...newPassFlowItems,
        ];

        // Create new multipass section with updated settings
        const newMultipassSection: BeaconWeldingData.FlowItemMultipassSectionParent =
          {
            ...multipassSection,
            attributes: {
              ...multipassSection.attributes,
              multipassSettings,
              originalItems: updatedOriginalItems,
              endItemId:
                allMultipassFlowItems.length > 0
                  ? allMultipassFlowItems[allMultipassFlowItems.length - 1].id
                  : undefined,
            },
          };

        const newMultipassFlow = [
          newMultipassSection,
          ...allMultipassFlowItems,
        ];

        // Replace the old multipass section with the new one in the flow
        const currentFlowItems = flowItems.filter(
          (item) => !instanceOfPlaceholder(item)
        ) as FlowItem[];

        const sectionStartIndex = currentFlowItems.findIndex(
          (item) => item.id === multipassSection.id
        );

        if (sectionStartIndex === -1) {
          logger.error('Could not find multipass section in flow', {
            sectionId: multipassSection.id,
          });
          return updatedRootItems;
        }

        // Find the end of the multipass section
        const sectionEndIndex = multipassSection.attributes.endItemId
          ? currentFlowItems.findIndex(
              (item) => item.id === multipassSection.attributes.endItemId
            )
          : sectionStartIndex;

        // Handle case where endItemId exists but item is not found in currentFlowItems
        if (multipassSection.attributes.endItemId && sectionEndIndex === -1) {
          logger.error('Could not find multipass section end item in flow', {
            sectionId: multipassSection.id,
            endItemId: multipassSection.attributes.endItemId,
          });
          return updatedRootItems;
        }

        // Replace the section and its items with the new structure
        const updatedFlow = [
          ...currentFlowItems.slice(0, sectionStartIndex),
          ...newMultipassFlow,
          ...currentFlowItems.slice(sectionEndIndex + 1),
        ];

        logger.info('Successfully regenerated multipass section', {
          sectionId: multipassSection.id,
          newItemCount: newMultipassFlow.length,
        });

        return updatedFlow;
      } catch (error) {
        logger.error('Failed to regenerate multipass section', {
          sectionId: multipassSection.id,
          error,
        });
        // Return the original flow items if regeneration fails
        return updatedRootItems;
      }
    },
    [flowItemService]
  );

  return { regenerateMultipassSection };
};

export {
  useMultipassFormController,
  useManageMultipassScheduleFieldArray,
  getWeldSequenceOptions,
  generateMultipassedParts,
  generatePassFlowItems,
  createRootPassFlowItems,
  useConvertToMultipassFlowItems,
  nestMultipassItemsUnderSectionParent,
};
